import { useState, useMemo } from "react";
import { Check, ChevronsUpDown, Globe } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

// Comprehensive list of countries with dial codes
const countries = [
  { value: "af", label: "Afghanistan", code: "+93", flag: "🇦🇫" },
  { value: "al", label: "Albania", code: "+355", flag: "🇦🇱" },
  { value: "dz", label: "Algeria", code: "+213", flag: "🇩🇿" },
  { value: "ar", label: "Argentina", code: "+54", flag: "🇦🇷" },
  { value: "au", label: "Australia", code: "+61", flag: "🇦🇺" },
  { value: "at", label: "Austria", code: "+43", flag: "🇦🇹" },
  { value: "bh", label: "Bahrain", code: "+973", flag: "🇧🇭" },
  { value: "bd", label: "Bangladesh", code: "+880", flag: "🇧🇩" },
  { value: "be", label: "Belgium", code: "+32", flag: "🇧🇪" },
  { value: "br", label: "Brazil", code: "+55", flag: "🇧🇷" },
  { value: "ca", label: "Canada", code: "+1", flag: "🇨🇦" },
  { value: "cl", label: "Chile", code: "+56", flag: "🇨🇱" },
  { value: "cn", label: "China", code: "+86", flag: "🇨🇳" },
  { value: "co", label: "Colombia", code: "+57", flag: "🇨🇴" },
  { value: "hr", label: "Croatia", code: "+385", flag: "🇭🇷" },
  { value: "cz", label: "Czech Republic", code: "+420", flag: "🇨🇿" },
  { value: "dk", label: "Denmark", code: "+45", flag: "🇩🇰" },
  { value: "eg", label: "Egypt", code: "+20", flag: "🇪🇬" },
  { value: "et", label: "Ethiopia", code: "+251", flag: "🇪🇹" },
  { value: "fi", label: "Finland", code: "+358", flag: "🇫🇮" },
  { value: "fr", label: "France", code: "+33", flag: "🇫🇷" },
  { value: "de", label: "Germany", code: "+49", flag: "🇩🇪" },
  { value: "gh", label: "Ghana", code: "+233", flag: "🇬🇭" },
  { value: "gr", label: "Greece", code: "+30", flag: "🇬🇷" },
  { value: "hk", label: "Hong Kong", code: "+852", flag: "🇭🇰" },
  { value: "hu", label: "Hungary", code: "+36", flag: "🇭🇺" },
  { value: "in", label: "India", code: "+91", flag: "🇮🇳" },
  { value: "id", label: "Indonesia", code: "+62", flag: "🇮🇩" },
  { value: "ir", label: "Iran", code: "+98", flag: "🇮🇷" },
  { value: "iq", label: "Iraq", code: "+964", flag: "🇮🇶" },
  { value: "ie", label: "Ireland", code: "+353", flag: "🇮🇪" },
  { value: "il", label: "Israel", code: "+972", flag: "🇮🇱" },
  { value: "it", label: "Italy", code: "+39", flag: "🇮🇹" },
  { value: "jp", label: "Japan", code: "+81", flag: "🇯🇵" },
  { value: "jo", label: "Jordan", code: "+962", flag: "🇯🇴" },
  { value: "ke", label: "Kenya", code: "+254", flag: "🇰🇪" },
  { value: "kr", label: "South Korea", code: "+82", flag: "🇰🇷" },
  { value: "kw", label: "Kuwait", code: "+965", flag: "🇰🇼" },
  { value: "lb", label: "Lebanon", code: "+961", flag: "🇱🇧" },
  { value: "my", label: "Malaysia", code: "+60", flag: "🇲🇾" },
  { value: "mv", label: "Maldives", code: "+960", flag: "🇲🇻" },
  { value: "mx", label: "Mexico", code: "+52", flag: "🇲🇽" },
  { value: "ma", label: "Morocco", code: "+212", flag: "🇲🇦" },
  { value: "mm", label: "Myanmar", code: "+95", flag: "🇲🇲" },
  { value: "np", label: "Nepal", code: "+977", flag: "🇳🇵" },
  { value: "nl", label: "Netherlands", code: "+31", flag: "🇳🇱" },
  { value: "nz", label: "New Zealand", code: "+64", flag: "🇳🇿" },
  { value: "ng", label: "Nigeria", code: "+234", flag: "🇳🇬" },
  { value: "no", label: "Norway", code: "+47", flag: "🇳🇴" },
  { value: "om", label: "Oman", code: "+968", flag: "🇴🇲" },
  { value: "pk", label: "Pakistan", code: "+92", flag: "🇵🇰" },
  { value: "ph", label: "Philippines", code: "+63", flag: "🇵🇭" },
  { value: "pl", label: "Poland", code: "+48", flag: "🇵🇱" },
  { value: "pt", label: "Portugal", code: "+351", flag: "🇵🇹" },
  { value: "qa", label: "Qatar", code: "+974", flag: "🇶🇦" },
  { value: "ro", label: "Romania", code: "+40", flag: "🇷🇴" },
  { value: "ru", label: "Russia", code: "+7", flag: "🇷🇺" },
  { value: "sa", label: "Saudi Arabia", code: "+966", flag: "🇸🇦" },
  { value: "sg", label: "Singapore", code: "+65", flag: "🇸🇬" },
  { value: "za", label: "South Africa", code: "+27", flag: "🇿🇦" },
  { value: "es", label: "Spain", code: "+34", flag: "🇪🇸" },
  { value: "lk", label: "Sri Lanka", code: "+94", flag: "🇱🇰" },
  { value: "se", label: "Sweden", code: "+46", flag: "🇸🇪" },
  { value: "ch", label: "Switzerland", code: "+41", flag: "🇨🇭" },
  { value: "tw", label: "Taiwan", code: "+886", flag: "🇹🇼" },
  { value: "tz", label: "Tanzania", code: "+255", flag: "🇹🇿" },
  { value: "th", label: "Thailand", code: "+66", flag: "🇹🇭" },
  { value: "tr", label: "Turkey", code: "+90", flag: "🇹🇷" },
  { value: "ug", label: "Uganda", code: "+256", flag: "🇺🇬" },
  { value: "ua", label: "Ukraine", code: "+380", flag: "🇺🇦" },
  { value: "ae", label: "United Arab Emirates", code: "+971", flag: "🇦🇪" },
  { value: "uk", label: "United Kingdom", code: "+44", flag: "🇬🇧" },
  { value: "us", label: "United States", code: "+1", flag: "🇺🇸" },
  { value: "vn", label: "Vietnam", code: "+84", flag: "🇻🇳" },
  { value: "ye", label: "Yemen", code: "+967", flag: "🇾🇪" },
  { value: "zm", label: "Zambia", code: "+260", flag: "🇿🇲" },
  { value: "zw", label: "Zimbabwe", code: "+263", flag: "🇿🇼" },
  { value: "other", label: "Other", code: "", flag: "🌍" },
];

// Popular countries shown at top
const popularCountryValues = ["us", "uk", "ca", "au", "ae", "sa", "in"];

interface CountryComboboxProps {
  value: string;
  onValueChange: (value: string) => void;
  error?: string;
}

export function CountryCombobox({ value, onValueChange, error }: CountryComboboxProps) {
  const [open, setOpen] = useState(false);

  const selectedCountry = useMemo(
    () => countries.find((country) => country.value === value),
    [value]
  );

  const popularCountries = useMemo(
    () => countries.filter((c) => popularCountryValues.includes(c.value)),
    []
  );

  const otherCountries = useMemo(
    () => countries.filter((c) => !popularCountryValues.includes(c.value) && c.value !== "other"),
    []
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "w-full justify-between font-normal",
            !value && "text-muted-foreground",
            error && "border-destructive"
          )}
        >
          {selectedCountry ? (
            <span className="flex items-center gap-2">
              <span>{selectedCountry.flag}</span>
              <span>{selectedCountry.label}</span>
            </span>
          ) : (
            <span className="flex items-center gap-2">
              <Globe className="h-4 w-4" />
              Select your country
            </span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent 
        className="w-[300px] p-0 z-50 bg-popover border border-border shadow-lg rounded-lg" 
        side="bottom"
        align="start" 
        sideOffset={4}
        avoidCollisions={false}
      >
        <Command className="bg-popover">
          <CommandInput placeholder="Search country..." className="h-10 border-b border-border" />
          <CommandList className="max-h-60">
            <CommandEmpty>No country found.</CommandEmpty>
            <CommandGroup heading="Popular" className="p-1">
              {popularCountries.map((country) => (
                <CommandItem
                  key={country.value}
                  value={`${country.label} ${country.code}`}
                  onSelect={() => {
                    onValueChange(country.value);
                    setOpen(false);
                  }}
                  className="px-3 py-2 rounded-md cursor-pointer"
                >
                  <span className="mr-2">{country.flag}</span>
                  <span className="flex-1">{country.label}</span>
                  {country.code && (
                    <span className="text-muted-foreground text-xs">{country.code}</span>
                  )}
                  <Check
                    className={cn(
                      "ml-2 h-4 w-4",
                      value === country.value ? "opacity-100" : "opacity-0"
                    )}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandGroup heading="All Countries" className="p-1">
              {otherCountries.map((country) => (
                <CommandItem
                  key={country.value}
                  value={`${country.label} ${country.code}`}
                  onSelect={() => {
                    onValueChange(country.value);
                    setOpen(false);
                  }}
                  className="px-3 py-2 rounded-md cursor-pointer"
                >
                  <span className="mr-2">{country.flag}</span>
                  <span className="flex-1">{country.label}</span>
                  {country.code && (
                    <span className="text-muted-foreground text-xs">{country.code}</span>
                  )}
                  <Check
                    className={cn(
                      "ml-2 h-4 w-4",
                      value === country.value ? "opacity-100" : "opacity-0"
                    )}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandGroup className="p-1">
              <CommandItem
                value="other"
                onSelect={() => {
                  onValueChange("other");
                  setOpen(false);
                }}
                className="px-3 py-2 rounded-md cursor-pointer"
              >
                <span className="mr-2">🌍</span>
                <span className="flex-1">Other</span>
                <Check
                  className={cn(
                    "ml-2 h-4 w-4",
                    value === "other" ? "opacity-100" : "opacity-0"
                  )}
                />
              </CommandItem>
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
